import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import {
  adminPlanTierApi,
  adminPlanPriceApi
} from '../../api/adminSubscriptionPlan.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import PlanPriceRow from './PlanPriceRow';
import StripeIdField from './StripeIdField';
import { summarizeTierStripe } from './planStripeStatus';
import './subscription-plans.css';

let draftCounter = 0;

function makeDraftPrice(tierSlug = '', index = 0) {
  draftCounter += 1;
  const base = tierSlug
    ? tierSlug.toUpperCase().replace(/[^A-Z0-9]/g, '_')
    : 'PLAN';
  return {
    _draftKey: `draft-${draftCounter}`,
    code: `${base}_MONTHLY`,
    interval: 'month',
    price: '',
    currency: 'USD',
    displayOrder: index,
    isActive: true,
    stripeProductId: '',
    stripePriceId: ''
  };
}

const emptyCreateForm = {
  tier: '',
  name: '',
  subtitle: '',
  description: '',
  includedMembers: 1,
  additionalMemberPrice: '',
  maxContacts: '',
  displayOrder: 0,
  isPopular: false,
  isActive: true,
  planNotes: [''],
  includeNotes: [{ label: '', included: true }],
  referralGroupDiscountPercent: '',
  sponsorGroupDiscountPercent: ''
};

function normalizeIncludeNotes(rows) {
  if (!Array.isArray(rows) || !rows.length) {
    return [{ label: '', included: true }];
  }
  return rows.map((row) => ({
    label: row?.label || '',
    included: row?.included !== false,
    ...(row?.key ? { key: row.key } : {})
  }));
}

function buildTierPayload(form) {
  return {
    tier: form.tier,
    name: form.name,
    subtitle: form.subtitle,
    description: form.description,
    planNotes: (form.planNotes || []).filter(Boolean),
    includeNotes: normalizeIncludeNotes(form.includeNotes).filter((row) => row.label.trim()),
    includedMembers: Number(form.includedMembers),
    additionalMemberPrice: form.additionalMemberPrice === '' ? null : Number(form.additionalMemberPrice),
    maxContacts: form.maxContacts === '' ? null : Number(form.maxContacts),
    isPopular: !!form.isPopular,
    displayOrder: Number(form.displayOrder || 0),
    isActive: !!form.isActive,
    referralGroupDiscountPercent: form.referralGroupDiscountPercent === ''
      ? null
      : Number(form.referralGroupDiscountPercent),
    sponsorGroupDiscountPercent: form.sponsorGroupDiscountPercent === ''
      ? null
      : Number(form.sponsorGroupDiscountPercent)
  };
}

function buildPricePayload(price) {
  return {
    code: price.code,
    interval: price.interval,
    price: price.price === '' ? 0 : Number(price.price),
    currency: price.currency || 'USD',
    displayOrder: Number(price.displayOrder ?? 0),
    isActive: price.isActive !== false
  };
}

export default function PlanTierEditor() {
  const { tierId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isNew = tierId === 'new' || pathname.endsWith('/plans/new/edit') || pathname.endsWith('/plan-tiers/new/edit');
  const [form, setForm] = useState(isNew ? emptyCreateForm : null);
  const [prices, setPrices] = useState(isNew ? [makeDraftPrice('', 0)] : []);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [priceSavingKey, setPriceSavingKey] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      setLoading(true);
      const result = await apiHandler(() => adminPlanTierApi.getById(tierId));
      if (result.success) {
        const data = unwrapData(result);
        setForm({
          ...data,
          planNotes: Array.isArray(data.planNotes) && data.planNotes.length ? data.planNotes : [''],
          includeNotes: normalizeIncludeNotes(data.includeNotes),
          referralGroupDiscountPercent: data.referralGroupDiscountPercent ?? '',
          sponsorGroupDiscountPercent: data.sponsorGroupDiscountPercent ?? ''
        });
        setPrices(data.prices || []);
      } else {
        setError(result.message || 'Failed to load plan');
      }
      setLoading(false);
    };
    load();
  }, [tierId, isNew]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateNote = (index, value) => {
    setForm((prev) => {
      const planNotes = [...prev.planNotes];
      planNotes[index] = value;
      return { ...prev, planNotes };
    });
  };

  const addNote = () => {
    setForm((prev) => ({ ...prev, planNotes: [...prev.planNotes, ''] }));
  };

  const removeNote = (index) => {
    setForm((prev) => ({
      ...prev,
      planNotes: prev.planNotes.filter((_, i) => i !== index)
    }));
  };

  const updateIncludeNote = (index, key, value) => {
    setForm((prev) => {
      const includeNotes = [...normalizeIncludeNotes(prev.includeNotes)];
      includeNotes[index] = { ...includeNotes[index], [key]: value };
      return { ...prev, includeNotes };
    });
  };

  const addIncludeNote = () => {
    setForm((prev) => ({
      ...prev,
      includeNotes: [...normalizeIncludeNotes(prev.includeNotes), { label: '', included: true }]
    }));
  };

  const removeIncludeNote = (index) => {
    setForm((prev) => {
      const includeNotes = normalizeIncludeNotes(prev.includeNotes).filter((_, i) => i !== index);
      return {
        ...prev,
        includeNotes: includeNotes.length ? includeNotes : [{ label: '', included: true }]
      };
    });
  };

  const updatePriceAt = (key, updated) => {
    setPrices((prev) => prev.map((p) => {
      const matchKey = p.id || p._draftKey;
      return matchKey === key ? { ...p, ...updated } : p;
    }));
  };

  const addPriceRow = () => {
    setPrices((prev) => [...prev, makeDraftPrice(form?.tier || '', prev.length)]);
  };

  const removeDraftPrice = (key) => {
    setPrices((prev) => prev.filter((p) => (p.id || p._draftKey) !== key));
  };

  const savePlan = async () => {
    setSaving(true);
    setError('');
    setMessage('');

    const validateDiscount = (value, label) => {
      if (value === '' || value === null || value === undefined) return null;
      const num = Number(value);
      if (!Number.isFinite(num) || num < 0 || num > 100) {
        return `${label} must be between 0 and 100`;
      }
      return null;
    };

    const referralErr = validateDiscount(form.referralGroupDiscountPercent, 'Referral group discount');
    const sponsorErr = validateDiscount(form.sponsorGroupDiscountPercent, 'Sponsor group discount');
    if (referralErr || sponsorErr) {
      setError(referralErr || sponsorErr);
      setSaving(false);
      return;
    }

    const payload = buildTierPayload(form);
    if (isNew) {
      const priceRows = prices
        .filter((p) => p.code && p.interval && p.price !== '')
        .map(buildPricePayload);
      payload.prices = priceRows;
    }

    const result = await apiHandler(() => (
      isNew
        ? adminPlanTierApi.create(payload)
        : adminPlanTierApi.update(tierId, payload)
    ));

    if (result.success) {
      const data = unwrapData(result);
      if (isNew) {
        navigate(`/plans/${data.id}/edit`);
      } else {
        setForm((prev) => ({
          ...prev,
          ...data,
          planNotes: Array.isArray(data.planNotes) && data.planNotes.length ? data.planNotes : [''],
          includeNotes: normalizeIncludeNotes(data.includeNotes),
          referralGroupDiscountPercent: data.referralGroupDiscountPercent ?? '',
          sponsorGroupDiscountPercent: data.sponsorGroupDiscountPercent ?? ''
        }));
        setPrices(data.prices || []);
        setMessage('Plan saved successfully');
      }
    } else {
      setError(result.message || 'Save failed');
    }
    setSaving(false);
  };

  const saveExistingPrice = async (price) => {
    const key = price.id || price._draftKey;
    setPriceSavingKey(key);
    setError('');
    setMessage('');

    const payload = buildPricePayload(price);
    const result = await apiHandler(() => adminPlanPriceApi.update(price.id, payload));

    if (result.success) {
      const data = unwrapData(result);
      setPrices((prev) => prev.map((p) => (p.id === price.id ? data : p)));
      if (data.stripeProductId) {
        setForm((prev) => ({ ...prev, stripeProductId: data.stripeProductId }));
      }
      const stripeNote = Number(data.price) > 0 && data.stripePriceId
        ? ' Stripe product/price synced.'
        : '';
      setMessage(`Price saved — ${data.code}.${stripeNote}`);
    } else {
      setError(result.message || 'Failed to save price');
    }
    setPriceSavingKey(null);
  };

  const createPrice = async (price) => {
    const key = price._draftKey;
    setPriceSavingKey(key);
    setError('');
    setMessage('');

    const payload = {
      planTierId: tierId,
      ...buildPricePayload(price)
    };
    const result = await apiHandler(() => adminPlanPriceApi.create(payload));

    if (result.success) {
      const data = unwrapData(result);
      setPrices((prev) => prev.map((p) => (p._draftKey === key ? data : p)));
      if (data.stripeProductId) {
        setForm((prev) => ({ ...prev, stripeProductId: data.stripeProductId }));
      }
      const stripeNote = Number(data.price) > 0 && data.stripePriceId
        ? ' Stripe product/price created.'
        : '';
      setMessage(`Price added — ${data.code}.${stripeNote}`);
    } else {
      setError(result.message || 'Failed to add price');
    }
    setPriceSavingKey(null);
  };

  const deactivatePrice = async (price) => {
    if (!window.confirm(`Deactivate price "${price.code}"?`)) return;

    const key = price.id || price._draftKey;
    setPriceSavingKey(key);
    setError('');
    setMessage('');

    const result = await apiHandler(() => adminPlanPriceApi.deactivate(price.id));

    if (result.success) {
      const data = unwrapData(result);
      setPrices((prev) => prev.map((p) => (p.id === price.id ? data : p)));
      setMessage(`Price deactivated — ${data.code}`);
    } else {
      setError(result.message || 'Failed to deactivate price');
    }
    setPriceSavingKey(null);
  };

  if (loading) {
    return (
      <div className="sp-page sp-loading">
        <i className="fa-solid fa-spinner fa-spin" /> Loading plan...
      </div>
    );
  }

  if (!form) {
    return (
      <div className="sp-page sp-empty">
        <p>Plan not found.</p>
        <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/plans')}>
          Back to plans
        </button>
      </div>
    );
  }

  const stripeSummary = summarizeTierStripe(prices, form.stripeProductId);
  const subscriberCount = form.subscriberCount ?? 0;

  return (
    <div className="sp-page">
      <PageHeader
        title={isNew ? 'Create Subscription Plan' : `Edit ${form.name}`}
        subtitle={isNew ? 'Add a new billing plan' : `${form.tier} · billing page`}
        actions={(
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/plans')}>
            <i className="fa-solid fa-arrow-left" /> All plans
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}
      {message && <div className="sp-alert sp-alert-success">{message}</div>}

      {!isNew && (
        <div className="sp-plan-overview">
          <div className="sp-overview-card">
            <span className="sp-overview-label">Plan status</span>
            <strong className={form.isActive ? 'sp-text-success' : 'sp-text-muted'}>
              {form.isActive ? 'Active' : 'Inactive'}
            </strong>
          </div>
          <div className="sp-overview-card">
            <span className="sp-overview-label">Active prices</span>
            <strong>{stripeSummary.activeCount}</strong>
          </div>
          <div className="sp-overview-card">
            <span className="sp-overview-label">Stripe synced</span>
            <strong className={stripeSummary.allPaidSynced ? 'sp-text-success' : 'sp-text-warning'}>
              {stripeSummary.paidCount === 0
                ? 'N/A (free only)'
                : `${stripeSummary.syncedCount}/${stripeSummary.paidCount}`}
            </strong>
          </div>
          <div className="sp-overview-card">
            <span className="sp-overview-label">Subscribers</span>
            <strong>{subscriberCount}</strong>
          </div>
        </div>
      )}

      {!isNew && (
        <div className="sp-info-callout">
          <div className="sp-info-callout-icon">
            <i className="fa-solid fa-circle-info" />
          </div>
          <div>
            <strong>How billing works</strong>
            <ol className="sp-info-steps">
              <li>Customer picks this plan on the app → review API returns a <strong>Checkout ID</strong> (shown on each price row).</li>
              <li>App calls <code>POST /billing/checkout-session</code> with that ID.</li>
              <li>Paid plans → Stripe Checkout opens. Free plans → instant activation.</li>
              <li>When you change price or interval here, click <strong>Save &amp; sync to Stripe</strong> — a new Stripe price is created automatically.</li>
            </ol>
          </div>
        </div>
      )}

      <div className="sp-form-stack">
        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-id-card" />
            <h4>Plan details</h4>
          </div>
          <div className="sp-form-grid-2">
            {isNew && (
              <div className="sp-form-row">
                <label>Plan code (slug)</label>
                <input
                  value={form.tier || ''}
                  onChange={(e) => updateField('tier', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="e.g. enterprise"
                />
              </div>
            )}
            <div className="sp-form-row">
              <label>Plan name</label>
              <input value={form.name || ''} onChange={(e) => updateField('name', e.target.value)} />
            </div>
            <div className="sp-form-row">
              <label>Subtitle</label>
              <input value={form.subtitle || ''} onChange={(e) => updateField('subtitle', e.target.value)} placeholder="e.g. (Family Plan)" />
            </div>
            <div className="sp-form-row">
              <label>Display order</label>
              <input type="number" value={form.displayOrder ?? 0} onChange={(e) => updateField('displayOrder', e.target.value)} />
            </div>
          </div>
          <div className="sp-form-row">
            <label>Description</label>
            <textarea rows={3} value={form.description || ''} onChange={(e) => updateField('description', e.target.value)} />
          </div>
          <div className="sp-toggle-row">
            <label className="sp-toggle">
              <input type="checkbox" checked={!!form.isPopular} onChange={(e) => updateField('isPopular', e.target.checked)} />
              Highlight as popular
            </label>
            <label className="sp-toggle">
              <input type="checkbox" checked={!!form.isActive} onChange={(e) => updateField('isActive', e.target.checked)} />
              Active on billing page
            </label>
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-users" />
            <h4>Members & limits</h4>
          </div>
          <div className="sp-form-grid-2">
            <div className="sp-form-row">
              <label>Included members</label>
              <input type="number" value={form.includedMembers ?? 1} onChange={(e) => updateField('includedMembers', e.target.value)} />
            </div>
            <div className="sp-form-row">
              <label>Additional member price ($/mo)</label>
              <input type="number" step="0.01" value={form.additionalMemberPrice ?? ''} onChange={(e) => updateField('additionalMemberPrice', e.target.value)} />
            </div>
            <div className="sp-form-row">
              <label>Max contacts (internal)</label>
              <input type="number" value={form.maxContacts ?? ''} onChange={(e) => updateField('maxContacts', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-percent" />
            <h4>Group referral discounts</h4>
          </div>
          <p className="sp-section-hint">
            Plan-wise discount when a user joins via a referral group code or sponsor group referral.
            Leave blank for no discount on that plan.
          </p>
          <div className="sp-form-grid-2">
            <div className="sp-form-row">
              <label>Referral group discount (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.referralGroupDiscountPercent ?? ''}
                onChange={(e) => updateField('referralGroupDiscountPercent', e.target.value)}
                placeholder="e.g. 15"
              />
              {form.referralGroupDiscountPercent !== '' && (
                Number(form.referralGroupDiscountPercent) < 0
                || Number(form.referralGroupDiscountPercent) > 100
              ) && (
                <span className="sp-field-error">Discount must be between 0 and 100</span>
              )}
            </div>
            <div className="sp-form-row">
              <label>Sponsor group discount (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.sponsorGroupDiscountPercent ?? ''}
                onChange={(e) => updateField('sponsorGroupDiscountPercent', e.target.value)}
                placeholder="e.g. 20"
              />
            </div>
          </div>
        </div>

        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-tags" />
            <h4>Pricing &amp; Stripe</h4>
          </div>
          <p className="sp-section-hint">
            Each price row is a purchasable billing option. Paid prices are synced to Stripe when you save.
            Do not edit Stripe IDs manually — they are created and updated automatically.
          </p>

          {!isNew && form.stripeProductId && (
            <div className="sp-tier-stripe-product">
              <StripeIdField
                label="Tier Stripe product (shared by all prices)"
                value={form.stripeProductId}
                hint="One Stripe product per plan tier. All monthly/annual prices link to this product."
                linkType="product"
              />
            </div>
          )}

          {stripeSummary.pendingCount > 0 && (
            <div className="sp-alert sp-alert-warning">
              <i className="fa-solid fa-triangle-exclamation" />
              {' '}
              {stripeSummary.pendingCount} paid price(s) not synced to Stripe yet.
              Save each price row to create Stripe billing.
            </div>
          )}

          {prices.length === 0 ? (
            <p className="sp-muted sp-price-empty">No prices yet. Click &quot;Add price&quot; to create one.</p>
          ) : (
            prices.map((price) => {
              const rowKey = price.id || price._draftKey;
              const isDraft = !price.id;
              const rowSaving = priceSavingKey === rowKey;

              return (
                <PlanPriceRow
                  key={rowKey}
                  price={price}
                  isDraft={isDraft}
                  saving={rowSaving}
                  onChange={(updated) => updatePriceAt(rowKey, updated)}
                  onSave={isNew
                    ? null
                    : () => {
                        const current = prices.find((p) => (p.id || p._draftKey) === rowKey);
                        if (!current) return;
                        if (isDraft) createPrice(current);
                        else saveExistingPrice(current);
                      }}
                  onRemove={isNew
                    ? () => removeDraftPrice(rowKey)
                    : () => {
                        const current = prices.find((p) => (p.id || p._draftKey) === rowKey);
                        if (!current) return;
                        if (isDraft) removeDraftPrice(rowKey);
                        else deactivatePrice(current);
                      }}
                />
              );
            })
          )}

          <button type="button" className="sp-btn sp-btn-secondary sp-btn-sm" onClick={addPriceRow}>
            <i className="fa-solid fa-plus" /> Add price
          </button>
        </div>

        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-circle-check" />
            <h4>Included features</h4>
          </div>
          <p className="sp-section-hint">
            Billing page &quot;Included&quot; list — each row has a label and included flag (checkmark or X).
          </p>
          <div className="sp-feature-list">
            {normalizeIncludeNotes(form.includeNotes).map((row, index) => (
              <div
                className={`sp-feature-row ${row.included ? 'included' : 'excluded'}`}
                key={index}
              >
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateIncludeNote(index, 'label', e.target.value)}
                  placeholder="e.g. Trusted Circle Assist"
                />
                <label className={`sp-included-toggle ${row.included ? 'yes' : 'no'}`}>
                  <input
                    type="checkbox"
                    checked={!!row.included}
                    onChange={(e) => updateIncludeNote(index, 'included', e.target.checked)}
                  />
                  {row.included ? 'Included' : 'Not included'}
                </label>
                {normalizeIncludeNotes(form.includeNotes).length > 1 && (
                  <button
                    type="button"
                    className="sp-note-remove"
                    onClick={() => removeIncludeNote(index)}
                    title="Remove feature"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="sp-btn sp-btn-secondary sp-btn-sm" onClick={addIncludeNote}>
            <i className="fa-solid fa-plus" /> Add feature
          </button>
        </div>

        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-list-ul" />
            <h4>Plan notes</h4>
          </div>
          <p className="sp-section-hint">Bullet points shown for this plan on the billing page.</p>
          {(form.planNotes || ['']).map((note, index) => (
            <div className="sp-note-row" key={index}>
              <textarea rows={2} value={note} onChange={(e) => updateNote(index, e.target.value)} placeholder="Plan note..." />
              {(form.planNotes || []).length > 1 && (
                <button type="button" className="sp-note-remove" onClick={() => removeNote(index)} title="Remove note">
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          ))}
          <button type="button" className="sp-btn sp-btn-secondary sp-btn-sm" onClick={addNote}>
            <i className="fa-solid fa-plus" /> Add note
          </button>
        </div>

        <div className="sp-actions">
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/plans')}>
            Cancel
          </button>
          <button type="button" className="sp-btn sp-btn-primary" disabled={saving} onClick={savePlan}>
            {saving ? 'Saving...' : isNew ? 'Create plan' : 'Save plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
